import {
  CustomTaskDescription,
  CustomTaskDescriptionChange,
} from "@coworker/apprestructured/src/task-management/components/CustomTaskDescription/CustomTaskDescription";
import ChecklistItem from "@coworker/apprestructured/src/task-management/types/ChecklistItem";
import EmptyObject from "@coworker/apprestructured/src/task-management/types/EmptyObject";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { getByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { Ref, useCallback, useImperativeHandle } from "react";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import { validateInitialDetailsFieldValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";

export type CustomTaskDescriptionCreatorFragmentConfigProps = EmptyObject;

type CustomTaskDescriptionCreatorFragmentProps = {
  configProps: CustomTaskDescriptionCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

function CustomTaskDescriptionCreatorFragment({
  commonProps,
  ref,
}: CustomTaskDescriptionCreatorFragmentProps) {
  const { blockInput, task, onTaskChange, showValidation, taskType } =
    commonProps;

  // Validate and provide defaults based on schema's initial values
  const title =
    validateInitialDetailsFieldValue<string>(
      getByPath(task, "details.title"),
      "details.title",
      taskType
    ) ?? "";

  const description =
    validateInitialDetailsFieldValue<string>(
      getByPath(task, "details.description"),
      "details.description",
      taskType
    ) ?? "";

  const checklist =
    validateInitialDetailsFieldValue<ChecklistItem[]>(
      getByPath(task, "details.checklist"),
      "details.checklist",
      taskType
    ) ?? [];

  const handleConfirmClick = useCallback(
    ({ title, description, checklist }: CustomTaskDescriptionChange) => {
      if (title !== undefined) {
        onTaskChange({ details: { title } });
      }
      if (description !== undefined) {
        onTaskChange({ details: { description } });
      }
      if (checklist !== undefined) {
        onTaskChange({ details: { checklist } });
      }
    },
    [onTaskChange]
  );

  useImperativeHandle(ref, () => {
    return { valid: !isLoading(title) && !!title };
  });

  if (isLoading(title) || isLoading(description) || isLoading(checklist)) {
    return <LoadingCreatorFragment />;
  }

  return (
    <CustomTaskDescription
      title={title}
      description={description}
      checklist={checklist}
      onConfirmClick={handleConfirmClick}
      disabled={blockInput}
      showValidation={showValidation}
    />
  );
}

export default CustomTaskDescriptionCreatorFragment;
